import React, { useState, useRef, useEffect } from "react";
import { updateDoc, doc, getDoc } from "firebase/firestore";
import { db } from "../firebase-config";
import {
  Button, Table, TableBody, TableCell, TableRow, Typography,
  Dialog, DialogTitle, DialogContent, DialogActions, TableContainer,
  TableHead, IconButton, TextField, FormControl, InputLabel, Select,
  MenuItem, Box, Chip, Divider, Paper, alpha, useTheme, Checkbox,
  FormControlLabel
} from "@mui/material";
import useMediaQuery from '@mui/material/useMediaQuery';
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import html2pdf from "html2pdf.js";
import appTheme from "../theme";

const InvoiceModal = ({ invoice, isModalOpen, handleClose }) => {
  // ✅ ADD RESPONSIVE HOOKS
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [editableInvoice, setEditableInvoice] = useState(invoice);
  const [editingIndex, setEditingIndex] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const [editField, setEditField] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const invoiceRef = useRef();

  console.log("invoice prop:", invoice);

  // also log editableInvoice when it updates so we can see whether
  // enrichment or fallbacks kicked in
  useEffect(() => {
    console.log("editableInvoice state:", editableInvoice);
  }, [editableInvoice]);


  const safeToFixed = (value, decimals = 2) => {
    if (value === undefined || value === null || isNaN(value)) return '0.00';
    return Number(value).toFixed(decimals);
  };

  // convert any value (string/number) into a usable number, stripping
  // currency symbols, commas, etc. falls back to 0 for invalid input.
  const safeNumber = (value) => {
    if (value === undefined || value === null) return 0;
    let num = value;
    if (typeof num === 'string') {
      // remove anything except digits, minus sign, decimal point
      num = num.replace(/[^0-9.\-]+/g, "");
    }
    num = Number(num);
    return isNaN(num) ? 0 : num;
  };

  useEffect(() => {
    // if the incoming invoice is missing some customer fields, use
    // alternate names or empty string
    const populated = {
      ...invoice,
      restaurantName: invoice.restaurantName || invoice.name || "",
      email: invoice.email || invoice.restaurantEmail || "",
      phone: invoice.phone || invoice.restaurantPhone || "",
      address: invoice.address || invoice.restaurantAddress || "",
    };

    // function to fill item prices from inventory when unavailable
    const enrichItems = async (inv) => {
      if (!inv.items || !Array.isArray(inv.items)) return inv;
      const newItems = await Promise.all(inv.items.map(async (item) => {
        // convert any string prices to number
        let basePrice = safeNumber(item.priceExclVAT || item.price);

        if (basePrice <= 0 && item.refId) {
          try {
            const col = item.collection || "inventoryItems";
            const itemSnap = await getDoc(doc(db, col, item.refId));
            if (itemSnap.exists()) {
              const data = itemSnap.data();
              const resolved = safeNumber(data.sellingPrice || data.price);
              // update hasVAT based on inventory regardless of what invoice stored
              const actualHasVAT = Boolean(data.hasVAT);

              console.log("invoice-modal: enrich item", item.title || item.id, {
                invoiceHasVAT: item.hasVAT,
                inventoryHasVAT: actualHasVAT,
                resolvedPrice: resolved
              });

              if (resolved > 0) {
                basePrice = resolved;
                // mutate a copy of the item
                item = {
                  ...item,
                  price: resolved,
                  priceExclVAT: resolved,
                  hasVAT: actualHasVAT
                };
                if (actualHasVAT) {
                  const vatCalc = calculateVATForItem(resolved, true);
                  item = { ...item, ...vatCalc };
                } else {
                  item.priceInclVAT = resolved;
                  item.vatAmount = 0;
                }
              } else {
                // even if price isn't filled we still want correct hasVAT flag
                item.hasVAT = actualHasVAT;
              }
            }
          } catch (err) {
            console.error("Error fetching inventory item for invoice", err);
          }
        }
        return item;
      }));

      const updated = { ...inv, items: newItems };
      const totals = calculateInvoiceTotalsForInvoice(updated);
      return { ...updated, ...totals };
    };

    // run enrichment asynchronously and update state
    (async () => {
      const filled = await enrichItems(populated);

      // if recalculated totals differ from what's stored, patch the doc so
      // the list view stays in sync. this is a one‑time fix when the modal
      // opens; the user can still press Save Changes afterwards as usual.
      try {
        if (
          filled.id &&
          (filled.subtotal !== populated.subtotal ||
            filled.tax !== populated.tax ||
            filled.totalPrice !== populated.totalPrice)
        ) {
          const invoiceRef = doc(db, "invoices", filled.id);
          await updateDoc(invoiceRef, {
            subtotal: filled.subtotal,
            tax: filled.tax,
            totalPrice: filled.totalPrice,
          });
          console.log("invoice-modal: synced totals to firestore", {
            id: filled.id,
            subtotal: filled.subtotal,
            tax: filled.tax,
            totalPrice: filled.totalPrice,
          });
        }
      } catch (err) {
        console.error("invoice-modal: failed to sync totals", err);
      }

      setEditableInvoice(filled);
    })();
  }, [invoice]);

  // when the modal opens we sometimes only have a subset of data in the
  // invoice document (prices may not have been saved, restaurant info
  // stored separately, etc). try to fetch whatever is missing so the
  // detail view always shows complete information.
  useEffect(() => {
    if (!invoice) return;

    const fetchExtras = async () => {
      // fill in missing item prices from the inventory collection
      if (invoice.items && invoice.items.length) {
        const newItems = await Promise.all(
          invoice.items.map(async (item) => {
            const priceVal = safeNumber(item.price);
            if ((priceVal === 0 || priceVal === NaN) && item.refId) {
              try {
                const itemDoc = await getDoc(
                  doc(db, item.collection || "inventoryItems", item.refId)
                );
                if (itemDoc.exists()) {
                  const data = itemDoc.data();
                  return {
                    ...item,
                    price: data.sellingPrice || data.price || item.price,
                  };
                }
              } catch (e) {
                console.error("failed item lookup", e);
              }
            }
            return item;
          })
        );
        setEditableInvoice((prev) => ({ ...prev, items: newItems }));
      }

      // also try to pull basic restaurant/user info if missing
      if (
        (!invoice.email || !invoice.phone || !invoice.address) &&
        invoice.userId
      ) {
        try {
          const userDoc = await getDoc(doc(db, "users", invoice.userId));
          if (userDoc.exists()) {
            const d = userDoc.data();
            setEditableInvoice((prev) => ({
              ...prev,
              email: prev.email || d.email || "",
              phone: prev.phone || d.phone || "",
              address: prev.address || d.address || "",
            }));
          }
        } catch (e) {
          console.error("failed restaurant lookup", e);
        }
      }
    };

    fetchExtras();
  }, [invoice]);

  // ✅ ENHANCED VAT CALCULATION FUNCTION
  const calculateVATForItem = (priceExclVAT, hasVAT) => {
    const basePrice = safeNumber(priceExclVAT);
    const vatRate = 0.20; // 20% VAT

    if (!hasVAT || basePrice <= 0) {
      return {
        priceExclVAT: basePrice,
        vatAmount: 0,
        priceInclVAT: basePrice,
        hasVAT: false
      };
    }

    const vatAmount = basePrice * vatRate;
    const priceInclVAT = basePrice + vatAmount;

    return {
      priceExclVAT: Number(basePrice.toFixed(2)),
      vatAmount: Number(vatAmount.toFixed(2)),
      priceInclVAT: Number(priceInclVAT.toFixed(2)),
      hasVAT: true
    };
  };

  const calculateItemTotal = (item) => {
    const quantity = safeNumber(item.quantity);
    const priceInclVAT = safeNumber(item.priceInclVAT || item.price);
    return quantity * priceInclVAT;
  };

  const calculateInvoiceTotals = () => {
    let subtotal = 0;
    let totalVAT = 0;

    editableInvoice.items.forEach(item => {
      const quantity = safeNumber(item.quantity);
      const priceExclVAT = safeNumber(item.priceExclVAT || item.price);
      const vatAmount = safeNumber(item.vatAmount);

      subtotal += quantity * priceExclVAT;
      totalVAT += quantity * vatAmount;
    });

    // if every item price was zero but the parent document contains
    // a non-zero subtotal/totalPrice, fall back to those values so the
    // modal totals still reflect the stored amounts.
    if (subtotal === 0 && safeNumber(editableInvoice.subtotal) > 0) {
      subtotal = safeNumber(editableInvoice.subtotal);
      totalVAT = safeNumber(editableInvoice.tax || editableInvoice.totalPrice - editableInvoice.subtotal);
    }

    return {
      subtotal,
      tax: totalVAT,
      totalPrice: subtotal + totalVAT
    };
  };

  const handleStatusChange = (field, value) => {
    setEditableInvoice((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const updateTotalPrice = () => {
    const totals = calculateInvoiceTotals();
    setEditableInvoice((prev) => ({
      ...prev,
      ...totals
    }));
  };
  // ✅ FIXED: Function to calculate totals for any invoice object
  const calculateInvoiceTotalsForInvoice = (invoiceObj) => {
    let subtotal = 0;
    let totalVAT = 0;

    invoiceObj.items.forEach(item => {
      const quantity = safeNumber(item.quantity);
      const priceExclVAT = safeNumber(item.priceExclVAT || item.price);
      const vatAmount = safeNumber(item.vatAmount);

      subtotal += quantity * priceExclVAT;
      totalVAT += quantity * vatAmount;
    });

    return {
      subtotal: Number(subtotal.toFixed(2)),
      tax: Number(totalVAT.toFixed(2)),
      totalPrice: Number((subtotal + totalVAT).toFixed(2))
    };
  };

  // ✅ ENHANCED EDIT FIELD HANDLER WITH VAT CALCULATION
  // ✅ FIXED: Single state update with immediate totals calculation
  const handleEditField = (field, index, value) => {
    setEditableInvoice((prevInvoice) => {
      // Create completely new invoice object
      const newInvoice = { ...prevInvoice };

      // Create new items array
      const newItems = [...newInvoice.items];

      // Create new item object
      const currentItem = { ...newItems[index] };

      // Update the field
      currentItem[field] = value;

      // Recalculate VAT if price changed
      if (field === 'priceExclVAT') {
        const vatCalculation = calculateVATForItem(value, currentItem.hasVAT);
        Object.assign(currentItem, vatCalculation);
      }

      // Update the item in the new array
      newItems[index] = currentItem;

      // Update the new invoice
      newInvoice.items = newItems;

      // ✅ FIXED: Calculate totals immediately in the same state update
      const updatedTotals = calculateInvoiceTotalsForInvoice(newInvoice);

      return {
        ...newInvoice,
        ...updatedTotals
      };
    });
  };



  const handleBlur = () => {
    if (editingIndex !== null && editField !== null) {
      let processedValue = inputValue;

      // Process value based on field type
      if (editField === 'quantity' || editField === 'priceExclVAT') {
        processedValue = safeNumber(inputValue);
      } else if (editField === 'hasVAT') {
        processedValue = inputValue === 'true' || inputValue === true;
      }

      handleEditField(editField, editingIndex, processedValue);
      setEditingIndex(null);
      setEditField(null);
      setInputValue("");
    }
  };

  // ✅ ENHANCED EDIT BUTTON COMPONENT
  const EditableField = ({
    value,
    field,
    index,
    type = 'text',
    options = null,
    displayValue = null
  }) => {
    const isEditing = editingIndex === index && editField === field;

    if (isEditing) {
      if (type === 'select') {
        return (
          <FormControl size="small" sx={{ minWidth: 80 }}>
            <Select
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onBlur={handleBlur}
              autoFocus
              sx={{
                fontSize: isMobile ? '0.75rem' : '0.875rem'
              }}
            >
              {options.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      } else if (type === 'checkbox') {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Checkbox
              checked={inputValue === 'true' || inputValue === true}
              onChange={(e) => setInputValue(e.target.checked)}
              size="small"
            />
            <IconButton
              size="small"
              onClick={handleBlur}
              sx={{ color: appTheme.colors.primary }}
            >
              <CheckIcon sx={{ fontSize: isMobile ? '14px' : '16px' }} />
            </IconButton>
          </Box>
        );
      } else {
        return (
          <TextField
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onBlur={handleBlur}
            onKeyPress={(e) => e.key === 'Enter' && handleBlur()}
            type={type}
            size="small"
            variant="outlined"
            autoFocus
            sx={{
              width: isMobile ? (type === 'number' ? '80px' : '120px') : 'auto',
              '& .MuiInputBase-input': {
                fontSize: isMobile ? '0.75rem' : '0.875rem'
              }
            }}
          />
        );
      }
    }

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" sx={{
          fontSize: isMobile ? '0.75rem' : '0.875rem'
        }}>
          {displayValue || value}
        </Typography>
        <IconButton
          size="small"
          className="edit-icon"
          onClick={() => {
            setEditingIndex(index);
            setEditField(field);
            setInputValue(type === 'checkbox' ? value : String(value || ''));
          }}
          sx={{
            padding: isMobile ? '2px' : '4px'
          }}
        >
          <EditIcon sx={{ fontSize: isMobile ? '14px' : '16px' }} />
        </IconButton>
      </Box>
    );
  };

  const handleSave = async () => {
    const invoiceDocRef = doc(db, "invoices", editableInvoice.id);
    try {
      await updateDoc(invoiceDocRef, editableInvoice);
      alert("Invoice updated successfully!");
      handleClose();
    } catch (err) {
      console.error("Error updating invoice: ", err);
    }
  };

  const handleSendInvoice = () => {
    setTimeout(() => {
      const element = invoiceRef.current;

      // **ENHANCED PDF OPTIONS FOR BETTER LAYOUT**
      const options = {
        margin: [0.5, 0.5, 0.5, 0.5], // Uniform margins
        filename: `Invoice_${editableInvoice.id}.pdf`,
        html2canvas: {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          scrollX: 0,
          scrollY: 0,
          windowWidth: 1024,
          windowHeight: 768
        },
        jsPDF: {
          unit: "in",
          format: "letter",
          orientation: "portrait",
          compress: true
        },
        pagebreak: {
          mode: ['avoid-all', 'css', 'legacy'],
          before: '.page-break-before',
          after: '.page-break-after',
          avoid: '.page-break-avoid'
        }
      };

      const clone = element.cloneNode(true);

      // **HIDE EDIT ICONS**
      const editIcons = clone.querySelectorAll(".edit-icon");
      editIcons.forEach((icon) => (icon.style.display = "none"));

      // **REPLACE DROPDOWNS WITH TEXT**
      const dropdowns = clone.querySelectorAll("select");
      dropdowns.forEach((dropdown) => {
        const selectedValue = dropdown.options[dropdown.selectedIndex].text;
        const span = document.createElement("span");
        span.textContent = selectedValue;
        span.style.fontSize = "14px";
        span.style.fontWeight = "500";
        dropdown.parentNode.replaceChild(span, dropdown);
      });

      // **REDUCE TABLE ROW HEIGHTS**
      const tableRows = clone.querySelectorAll("tr");
      tableRows.forEach((row) => {
        row.style.height = "auto";
        row.style.minHeight = "32px"; // **Reduced from default 48px**
        row.style.lineHeight = "1.2";
      });

      // **REDUCE TABLE CELL PADDING**
      const tableCells = clone.querySelectorAll("td, th");
      tableCells.forEach((cell) => {
        cell.style.padding = "6px 8px"; // **Reduced padding**
        cell.style.fontSize = "12px"; // **Consistent font size**
        cell.style.lineHeight = "1.3";
        cell.style.verticalAlign = "middle";
      });

      // **MAKE TABLE HEADERS MORE COMPACT**
      const headerCells = clone.querySelectorAll("th");
      headerCells.forEach((header) => {
        header.style.padding = "8px";
        header.style.fontSize = "12px";
        header.style.fontWeight = "bold";
        header.style.lineHeight = "1.2";
        header.style.height = "36px"; // **Fixed header height**
      });

      // **FIX TOTALS CONTAINER - PREVENT PAGE BREAK**
      const totalsContainer = clone.querySelector('[style*="border: 2px solid"]') ||
        clone.querySelector('[style*="backgroundColor"]');

      if (totalsContainer) {
        // **Add page break avoidance classes and styles**
        totalsContainer.classList.add('page-break-avoid');
        totalsContainer.style.pageBreakInside = 'avoid';
        totalsContainer.style.breakInside = 'avoid';
        totalsContainer.style.display = 'block';
        totalsContainer.style.overflow = 'visible';
        totalsContainer.style.minHeight = 'auto';
        totalsContainer.style.maxHeight = 'none';

        // **Ensure parent container also avoids break**
        const parentBox = totalsContainer.parentElement;
        if (parentBox) {
          parentBox.style.pageBreakInside = 'avoid';
          parentBox.classList.add('page-break-avoid');
        }

        // **Make totals section more compact**
        totalsContainer.style.padding = '12px';
        totalsContainer.style.margin = '12px 0';

        // **Reduce font sizes in totals**
        const totalTexts = totalsContainer.querySelectorAll('*');
        totalTexts.forEach(text => {
          if (text.style.fontSize) {
            const currentSize = parseInt(text.style.fontSize);
            if (currentSize > 12) {
              text.style.fontSize = '14px';
            } else if (currentSize <= 12) {
              text.style.fontSize = '12px';
            }
          }
          text.style.lineHeight = '1.4';
        });
      }

      // **MAKE STATUS CONTROLS MORE COMPACT**
      const statusControls = clone.querySelector('[style*="flex: 1"]');
      if (statusControls) {
        statusControls.style.marginBottom = '8px';
        statusControls.style.pageBreakInside = 'avoid';
        statusControls.classList.add('page-break-avoid');
      }

      // **ADD PAGE BREAK BEFORE TOTALS SECTION IF NEEDED**
      const totalsSection = clone.querySelector('[style*="justifyContent: space-between"]');
      if (totalsSection) {
        totalsSection.style.pageBreakInside = 'avoid';
        totalsSection.classList.add('page-break-avoid');

        // **Add a page break class that we can target**
        const pageBreakDiv = document.createElement('div');
        pageBreakDiv.className = 'totals-section-container';
        pageBreakDiv.style.pageBreakInside = 'avoid';
        pageBreakDiv.style.breakInside = 'avoid';
        pageBreakDiv.style.display = 'block';

        // **Wrap the totals section**
        totalsSection.parentNode.insertBefore(pageBreakDiv, totalsSection);
        pageBreakDiv.appendChild(totalsSection);
      }

      // **REDUCE OVERALL SPACING**
      const allBoxes = clone.querySelectorAll('[style*="marginBottom"]');
      allBoxes.forEach(box => {
        if (box.style.marginBottom) {
          const currentMargin = parseInt(box.style.marginBottom);
          if (currentMargin > 16) {
            box.style.marginBottom = '12px';
          }
        }
      });

      // **OPTIMIZE TABLE CONTAINER**
      const tableContainer = clone.querySelector('table');
      if (tableContainer) {
        tableContainer.style.borderCollapse = 'collapse';
        tableContainer.style.width = '100%';
        tableContainer.style.fontSize = '12px';

        // **Ensure table doesn't break awkwardly**
        const tableWrapper = tableContainer.closest('[component="Paper"]') ||
          tableContainer.parentElement;
        if (tableWrapper) {
          tableWrapper.style.pageBreakInside = 'auto';
          tableWrapper.style.overflow = 'visible';
        }
      }

      // **ADD CUSTOM CSS FOR BETTER PDF RENDERING**
      const style = document.createElement('style');
      style.textContent = `
      @media print {
        .page-break-avoid {
          page-break-inside: avoid !important;
          break-inside: avoid !important;
        }
        
        .totals-section-container {
          page-break-inside: avoid !important;
          break-inside: avoid !important;
          min-height: 120px;
        }
        
        table {
          border-collapse: collapse !important;
        }
        
        tr {
          page-break-inside: avoid;
          height: auto !important;
          min-height: 32px !important;
        }
        
        td, th {
          padding: 6px 8px !important;
          font-size: 12px !important;
          line-height: 1.3 !important;
          vertical-align: middle !important;
        }
        
        th {
          height: 36px !important;
          font-weight: bold !important;
        }
      }
    `;
      clone.appendChild(style);

      // **GENERATE PDF WITH ENHANCED OPTIONS**
      html2pdf()
        .from(clone)
        .set(options)
        .toPdf()
        .get('pdf')
        .then((pdf) => {
          // **Add page numbers if multiple pages**
          const pageCount = pdf.internal.getNumberOfPages();
          if (pageCount > 1) {
            for (let i = 1; i <= pageCount; i++) {
              pdf.setPage(i);
              pdf.setFontSize(10);
              pdf.text(
                `Page ${i} of ${pageCount}`,
                pdf.internal.pageSize.getWidth() - 50,
                pdf.internal.pageSize.getHeight() - 10
              );
            }
          }
        })
        .save();
    }, 100);
  };


  return (
    <Dialog
      open={isModalOpen}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : appTheme.borderRadius.rounded,
          maxHeight: isMobile ? '100vh' : '90vh',
          margin: isMobile ? 0 : undefined,
          width: isMobile ? '100vw' : undefined,
        }
      }}
      scroll="paper"
    >
      <DialogTitle sx={{
        background: appTheme.gradients.hero,
        color: "#FFFFFF",
        fontWeight: appTheme.typography.fontWeights.bold,
        padding: isMobile ? theme.spacing(2) : theme.spacing(3),
        fontSize: isMobile ? '1.1rem' : '1.25rem'
      }}>
        📄 Invoice Details
      </DialogTitle>

      <DialogContent sx={{
        padding: 0,
        overflowX: isMobile ? 'hidden' : 'auto'
      }}>
        <Box ref={invoiceRef} sx={{
          padding: isMobile ? theme.spacing(1.5) : appTheme.spacing.lg
        }}>
          {/* INVOICE HEADER - Same as before */}
          <Box sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'space-between',
            marginBottom: isMobile ? theme.spacing(2) : appTheme.spacing.xl,
            padding: isMobile ? theme.spacing(2) : appTheme.spacing.lg,
            backgroundColor: appTheme.colors.surfaceLuxury,
            borderRadius: appTheme.borderRadius.medium,
            gap: isMobile ? theme.spacing(2) : 0
          }}>
            <Box>
              <Typography variant="h6" sx={{
                fontWeight: 700,
                color: appTheme.colors.primary,
                fontSize: isMobile ? '1rem' : '1.25rem'
              }}>
                Bill From:
              </Typography>
              <Typography variant="body1" sx={{
                fontWeight: 600,
                fontSize: isMobile ? '0.875rem' : '1rem'
              }}>
                Digital Bot Solutions
              </Typography>
              <Typography variant="body2" sx={{
                color: appTheme.colors.textSecondary,
                fontSize: isMobile ? '0.75rem' : '0.875rem'
              }}>
                25-26, Kenton Park Parade, Kenton Rd, Harrow HA3 8DN
              </Typography>
            </Box>
            <Box sx={{ textAlign: isMobile ? 'left' : 'right' }}>
              <Typography variant="h6" sx={{
                fontWeight: 700,
                color: appTheme.colors.primary,
                fontSize: isMobile ? '1rem' : '1.25rem'
              }}>
                Invoice Date:
              </Typography>
              <Typography variant="body1" sx={{
                fontSize: isMobile ? '0.875rem' : '1rem'
              }}>
                {new Date(invoice.createdAt).toLocaleDateString('en-CA')}
              </Typography>
            </Box>
          </Box>

          {/* CUSTOMER DETAILS - Same as before */}
          <Box sx={{
            marginBottom: isMobile ? theme.spacing(2) : appTheme.spacing.xl,
            padding: isMobile ? theme.spacing(2) : appTheme.spacing.lg,
            backgroundColor: appTheme.colors.surface,
            borderRadius: appTheme.borderRadius.medium,
            border: `1px solid ${appTheme.colors.borderPrimary}`
          }}>
            <Typography variant="h6" sx={{
              fontWeight: 700,
              color: appTheme.colors.primary,
              marginBottom: 2,
              fontSize: isMobile ? '1rem' : '1.25rem'
            }}>
              Bill To:
            </Typography>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
              gap: isMobile ? 1 : 2
            }}>
              <Box>
                <Typography variant="body2" sx={{
                  fontWeight: 600,
                  fontSize: isMobile ? '0.75rem' : '0.875rem'
                }}>Name:</Typography>
                <Typography variant="body1" sx={{
                  fontSize: isMobile ? '0.875rem' : '1rem'
                }}>{
                    editableInvoice.restaurantName || editableInvoice.name || ""
                  }</Typography>
              </Box>
              <Box>
                <Typography variant="body2" sx={{
                  fontWeight: 600,
                  fontSize: isMobile ? '0.75rem' : '0.875rem'
                }}>Email:</Typography>
                <Typography variant="body1" sx={{
                  fontSize: isMobile ? '0.875rem' : '1rem'
                }}>{
                    editableInvoice.email || editableInvoice.restaurantEmail || ""
                  }</Typography>
              </Box>
              <Box>
                <Typography variant="body2" sx={{
                  fontWeight: 600,
                  fontSize: isMobile ? '0.75rem' : '0.875rem'
                }}>Address:</Typography>
                <Typography variant="body1" sx={{
                  fontSize: isMobile ? '0.875rem' : '1rem'
                }}>{
                    editableInvoice.address || editableInvoice.restaurantAddress || ""
                  }</Typography>
              </Box>
              <Box>
                <Typography variant="body2" sx={{
                  fontWeight: 600,
                  fontSize: isMobile ? '0.75rem' : '0.875rem'
                }}>Phone:</Typography>
                <Typography variant="body1" sx={{
                  fontSize: isMobile ? '0.875rem' : '1rem'
                }}>{
                    editableInvoice.phone || editableInvoice.restaurantPhone || ""
                  }</Typography>
              </Box>
            </Box>
          </Box>

          {/* ✅ ENHANCED ITEMS TABLE WITH FULL EDIT FUNCTIONALITY */}
          <TableContainer
            component={Paper}
            sx={{
              marginBottom: isMobile ? theme.spacing(2) : appTheme.spacing.xl,
              borderRadius: appTheme.borderRadius.medium,
              boxShadow: appTheme.shadows.soft,
              overflowX: isMobile ? 'auto' : 'visible',
              maxWidth: '100%'
            }}
          >
            <Table sx={{
              minWidth: isMobile ? 1000 : 'auto'
            }}>
              <TableHead sx={{ backgroundColor: appTheme.colors.primary }}>
                <TableRow>
                  <TableCell sx={{
                    color: "#FFFFFF",
                    fontWeight: 700,
                    fontSize: isMobile ? '0.75rem' : '0.875rem',
                    padding: isMobile ? '8px' : '16px'
                  }}>Title</TableCell>
                  <TableCell sx={{
                    color: "#FFFFFF",
                    fontWeight: 700,
                    fontSize: isMobile ? '0.75rem' : '0.875rem',
                    padding: isMobile ? '8px' : '16px'
                  }}>Brand</TableCell>
                  {/* <TableCell sx={{ 
                    color: "#FFFFFF", 
                    fontWeight: 700,
                    fontSize: isMobile ? '0.75rem' : '0.875rem',
                    padding: isMobile ? '8px' : '16px'
                  }}>Has VAT</TableCell> */}
                  <TableCell sx={{
                    color: "#FFFFFF",
                    fontWeight: 700,
                    fontSize: isMobile ? '0.75rem' : '0.875rem',
                    padding: isMobile ? '8px' : '16px'
                  }}>Price </TableCell>
                  <TableCell sx={{
                    color: "#FFFFFF",
                    fontWeight: 700,
                    fontSize: isMobile ? '0.75rem' : '0.875rem',
                    padding: isMobile ? '8px' : '16px'
                  }}>VAT Amount</TableCell>
                  <TableCell sx={{
                    color: "#FFFFFF",
                    fontWeight: 700,
                    fontSize: isMobile ? '0.75rem' : '0.875rem',
                    padding: isMobile ? '8px' : '16px'
                  }}>Total Price </TableCell>
                  <TableCell sx={{
                    color: "#FFFFFF",
                    fontWeight: 700,
                    fontSize: isMobile ? '0.75rem' : '0.875rem',
                    padding: isMobile ? '8px' : '16px'
                  }}>Quantity</TableCell>
                  <TableCell sx={{
                    color: "#FFFFFF",
                    fontWeight: 700,
                    fontSize: isMobile ? '0.75rem' : '0.875rem',
                    padding: isMobile ? '8px' : '16px'
                  }}>Units</TableCell>
                  <TableCell sx={{
                    color: "#FFFFFF",
                    fontWeight: 700,
                    fontSize: isMobile ? '0.75rem' : '0.875rem',
                    padding: isMobile ? '8px' : '16px'
                  }}>Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {editableInvoice.items.map((item, index) => (
                  <TableRow key={index} sx={{
                    "&:hover": {
                      backgroundColor: alpha(appTheme.colors.primary, 0.05)
                    }
                  }}>
                    {/* ✅ EDITABLE TITLE */}
                    <TableCell sx={{
                      padding: isMobile ? '8px' : '16px',
                      minWidth: isMobile ? '140px' : 'auto'
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <EditableField
                          value={item.title}
                          field="title"
                          index={index}
                          type="text"
                        />

                      </Box>
                    </TableCell>

                    {/* ✅ EDITABLE BRAND */}
                    <TableCell sx={{
                      padding: isMobile ? '8px' : '16px',
                      minWidth: isMobile ? '120px' : 'auto'
                    }}>
                      <EditableField
                        value={item.brand}
                        field="brand"
                        index={index}
                        type="text"
                      />
                    </TableCell>

                    {/* ✅ EDITABLE VAT STATUS */}
                    {/* <TableCell sx={{ 
                      padding: isMobile ? '8px' : '16px'
                    }}>
                      <EditableField
                        value={item.hasVAT}
                        field="hasVAT"
                        index={index}
                        type="checkbox"
                        displayValue={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {item.hasVAT ? (
                              <Chip 
                                label="Yes" 
                                size="small" 
                                sx={{
                                  backgroundColor: alpha(appTheme.colors.accent, 0.2),
                                  color: appTheme.colors.accent,
                                  fontSize: isMobile ? '0.5rem' : '0.6rem',
                                  height: isMobile ? '16px' : '20px'
                                }}
                              />
                            ) : (
                              <Chip 
                                label="No" 
                                size="small" 
                                sx={{
                                  backgroundColor: alpha(appTheme.colors.textSecondary, 0.2),
                                  color: appTheme.colors.textSecondary,
                                  fontSize: isMobile ? '0.5rem' : '0.6rem',
                                  height: isMobile ? '16px' : '20px'
                                }}
                              />
                            )}
                          </Box>
                        }
                      />
                    </TableCell> */}

                    {/* ✅ EDITABLE PRICE EXCL VAT */}
                    <TableCell sx={{
                      padding: isMobile ? '8px' : '16px'
                    }}>
                      <EditableField
                        value={item.priceExclVAT || item.price}
                        field="priceExclVAT"
                        index={index}
                        type="number"
                        displayValue={`£${safeToFixed(item.priceExclVAT || item.price)}`}
                      />
                    </TableCell>

                    {/* ✅ AUTO-CALCULATED VAT AMOUNT (READ-ONLY) */}
                    <TableCell sx={{
                      padding: isMobile ? '8px' : '16px'
                    }}>
                      <Typography variant="body2" sx={{
                        color: item.hasVAT ? appTheme.colors.accent : appTheme.colors.textSecondary,
                        fontWeight: 500,
                        fontSize: isMobile ? '0.75rem' : '0.875rem'
                      }}>
                        £{safeToFixed(item.vatAmount || 0)}
                      </Typography>
                    </TableCell>

                    {/* ✅ AUTO-CALCULATED PRICE INCL VAT (READ-ONLY) */}
                    <TableCell sx={{
                      padding: isMobile ? '8px' : '16px'
                    }}>
                      <Typography variant="body2" sx={{
                        fontWeight: 600,
                        color: item.hasVAT ? appTheme.colors.secondary : appTheme.colors.textPrimary,
                        fontSize: isMobile ? '0.75rem' : '0.875rem'
                      }}>
                        £{safeToFixed(item.priceInclVAT || item.price)}
                      </Typography>
                    </TableCell>

                    {/* ✅ EDITABLE QUANTITY */}
                    <TableCell sx={{
                      padding: isMobile ? '8px' : '16px'
                    }}>
                      <EditableField
                        value={item.quantity}
                        field="quantity"
                        index={index}
                        type="number"
                      />
                    </TableCell>

                    {/* ✅ EDITABLE UNITS */}
                    <TableCell sx={{
                      padding: isMobile ? '8px' : '16px'
                    }}>
                      <EditableField
                        value={item.units}
                        field="units"
                        index={index}
                        type="select"
                        options={[
                          { value: 'pcs', label: 'Pieces' },
                          { value: 'kg', label: 'Kilograms' },
                          { value: 'g', label: 'Grams' },
                          { value: 'litres', label: 'Litres' },
                          { value: 'ml', label: 'Millilitres' },
                          { value: 'box', label: 'Box' },
                          { value: 'pack', label: 'Pack' }
                        ]}
                      />
                    </TableCell>

                    {/* ✅ AUTO-CALCULATED ITEM TOTAL (READ-ONLY) */}
                    <TableCell sx={{
                      padding: isMobile ? '8px' : '16px'
                    }}>
                      <Typography variant="body2" sx={{
                        fontWeight: 700,
                        color: appTheme.colors.primary,
                        fontSize: isMobile ? '0.75rem' : '0.875rem'
                      }}>
                        £{safeToFixed(calculateItemTotal(item))}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* TOTALS SECTION - Same as before */}
          <Box sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'space-between',
            alignItems: isMobile ? 'stretch' : 'flex-start',
            marginBottom: isMobile ? theme.spacing(2) : appTheme.spacing.xl,
            gap: isMobile ? theme.spacing(2) : 0
          }}>
            {/* Status Controls */}
            <Box sx={{ flex: 1, order: isMobile ? 2 : 1 }}>
              {!showDropdown ? (
                <Box sx={{
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  gap: 2
                }}>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel sx={{
                      fontSize: isMobile ? '0.75rem' : '0.875rem'
                    }}>Order Status</InputLabel>
                    <Select
                      value={editableInvoice.orderStatus}
                      onChange={(e) => handleStatusChange("orderStatus", e.target.value)}
                      label="Order Status"
                      sx={{
                        fontSize: isMobile ? '0.75rem' : '0.875rem'
                      }}
                    >
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="accepted">Accepted</MenuItem>
                      <MenuItem value="shipped">Shipped</MenuItem>
                      <MenuItem value="delivered">Delivered</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel sx={{
                      fontSize: isMobile ? '0.75rem' : '0.875rem'
                    }}>Payment Status</InputLabel>
                    <Select
                      value={editableInvoice.isBillPaid ? "paid" : "pending"}
                      onChange={(e) => handleStatusChange("isBillPaid", e.target.value === "paid")}
                      label="Payment Status"
                      sx={{
                        fontSize: isMobile ? '0.75rem' : '0.875rem'
                      }}
                    >
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="paid">Paid</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              ) : (
                <Box>
                  <Typography variant="body1" sx={{
                    fontSize: isMobile ? '0.875rem' : '1rem'
                  }}>
                    <strong>Order Status:</strong> {editableInvoice.orderStatus}
                  </Typography>
                  <Typography variant="body1" sx={{
                    fontSize: isMobile ? '0.875rem' : '1rem'
                  }}>
                    <strong>Payment Status:</strong> {editableInvoice.isBillPaid ? "Paid" : "Pending"}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* ✅ AUTO-CALCULATED TOTALS */}
            <Box sx={{
              minWidth: isMobile ? '100%' : '300px',
              padding: isMobile ? theme.spacing(2) : appTheme.spacing.lg,
              backgroundColor: appTheme.colors.surfaceLuxury,
              borderRadius: appTheme.borderRadius.medium,
              border: `2px solid ${appTheme.colors.primary}`,
              order: isMobile ? 1 : 2
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: 1 }}>
                <Typography variant="body1" sx={{
                  fontWeight: 500,
                  fontSize: isMobile ? '0.875rem' : '1rem'
                }}>Subtotal:</Typography>
                <Typography variant="body1" sx={{
                  fontWeight: 600,
                  fontSize: isMobile ? '0.875rem' : '1rem'
                }}>
                  £{safeToFixed(calculateInvoiceTotals().subtotal)}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: 1 }}>
                <Typography variant="body1" sx={{
                  fontWeight: 500,
                  color: appTheme.colors.accent,
                  fontSize: isMobile ? '0.875rem' : '1rem'
                }}>
                  VAT (20%):
                </Typography>
                <Typography variant="body1" sx={{
                  fontWeight: 600,
                  color: appTheme.colors.accent,
                  fontSize: isMobile ? '0.875rem' : '1rem'
                }}>
                  £{safeToFixed(calculateInvoiceTotals().tax)}
                </Typography>
              </Box>

              <Divider sx={{ margin: '8px 0' }} />

              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="h6" sx={{
                  fontWeight: 700,
                  color: appTheme.colors.primary,
                  fontSize: isMobile ? '1rem' : '1.25rem'
                }}>
                  Total:
                </Typography>
                <Typography variant="h6" sx={{
                  fontWeight: 700,
                  color: appTheme.colors.primary,
                  fontSize: isMobile ? '1rem' : '1.25rem'
                }}>
                  £{safeToFixed(calculateInvoiceTotals().totalPrice)}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      {/* DIALOG ACTIONS - Same as before */}
      <DialogActions sx={{
        padding: isMobile ? theme.spacing(2) : appTheme.spacing.lg,
        backgroundColor: appTheme.colors.surfaceElevated,
        gap: isMobile ? 1 : 2,
        flexDirection: isMobile ? 'column' : 'row'
      }}>
        <Button
          onClick={handleClose}
          sx={{
            color: appTheme.colors.textSecondary,
            borderRadius: appTheme.borderRadius.medium,
            fontSize: isMobile ? '0.875rem' : '1rem',
            width: isMobile ? '100%' : 'auto'
          }}
        >
          Close
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          sx={{
            backgroundColor: appTheme.colors.primary,
            borderRadius: appTheme.borderRadius.medium,
            fontSize: isMobile ? '0.875rem' : '1rem',
            width: isMobile ? '100%' : 'auto',
            "&:hover": {
              backgroundColor: appTheme.colors.deepTeal,
            }
          }}
        >
          Save Changes
        </Button>
        <Button
          onClick={handleSendInvoice}
          variant="contained"
          sx={{
            backgroundColor: appTheme.colors.secondary,
            borderRadius: appTheme.borderRadius.medium,
            fontSize: isMobile ? '0.875rem' : '1rem',
            width: isMobile ? '100%' : 'auto',
            "&:hover": {
              backgroundColor: appTheme.colors.warmCopper,
            }
          }}
        >
          Download PDF
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InvoiceModal;
